import React, { useState, useRef, useCallback } from 'react';
import { Box, Stack, Typography, Popover, Menu, MenuItem } from '@mui/material';
import { SentimentSatisfiedAlt, DataObject } from '@mui/icons-material';
import Button from 'domains/core/components/Button';
import TextField from 'domains/core/components/TextField';
import { MergeTag } from 'domains/content/types';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { getFriendlyName } from 'utils';
import useContactColumns from 'hooks/queries/useContactColumns';
import { getMergeTags } from 'utils';
import { styles } from '../styles';

type EmojiData = {
    native: string;
    id?: string;
    name?: string;
    colons?: string;
    unified?: string;
};

type Props = {
    content: string;
    setContent: (content: string) => void;
};

const SmsEditor = ({ content, setContent }: Props) => {
    // Cursor position management
    const [cursorPosition, setCursorPosition] = useState({ start: 0, end: 0 });
    const textFieldRef = useRef<HTMLDivElement>(null);

    // Popup anchors
    const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [mergeTagAnchorEl, setMergeTagAnchorEl] = useState<HTMLButtonElement | null>(null);

    // Get merge tags
    const { data: contactColumns, isSuccess: isContactColumnsSuccess } = useContactColumns();
    const mergeTags = isContactColumnsSuccess ? getMergeTags(contactColumns) : [];
    const orderedMergeTags = mergeTags.sort((a, b) => a.value.localeCompare(b.value));

    // Store the current cursor position in the text field
    const storeCursorPosition = useCallback(() => {
        if (textFieldRef.current) {
            const input = textFieldRef.current.querySelector('textarea');
            if (input) {
                setCursorPosition({
                    start: input.selectionStart || 0,
                    end: input.selectionEnd || 0,
                });
            }
        }
    }, []);

    // Insert text at the current cursor position
    const insertAtCursor = useCallback(
        (insertText: string) => {
            const newContent =
                content.substring(0, cursorPosition.start) + insertText + content.substring(cursorPosition.end);
            setContent(newContent);

            // Set focus back to the input and place cursor after the inserted text
            setTimeout(() => {
                if (textFieldRef.current) {
                    const input = textFieldRef.current.querySelector('textarea');
                    if (input) {
                        input.focus();
                        const newPosition = cursorPosition.start + insertText.length;
                        setCursorPosition({ start: newPosition, end: newPosition });
                        input.setSelectionRange(newPosition, newPosition);
                    }
                }
            }, 0);
        },
        [content, cursorPosition, setContent]
    );

    // Event handlers
    const handleEmojiButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            storeCursorPosition();
            setEmojiAnchorEl(event.currentTarget);
        },
        [storeCursorPosition]
    );

    const handleEmojiSelect = useCallback(
        (emoji: EmojiData) => {
            insertAtCursor(emoji.native);
            setEmojiAnchorEl(null);
        },
        [insertAtCursor]
    );

    const handleMergeTagButtonClick = useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            storeCursorPosition();
            setMergeTagAnchorEl(event.currentTarget);
        },
        [storeCursorPosition]
    );

    const handleMergeTagSelect = useCallback(
        (value: string) => {
            insertAtCursor(value);
            setMergeTagAnchorEl(null);
        },
        [insertAtCursor]
    );

    const handleEmojiPickerClose = () => setEmojiAnchorEl(null);
    const handleMergeTagPickerClose = () => setMergeTagAnchorEl(null);

    return (
        <>
            <Typography variant="body1">
                Write a message that fits your goal. Keep it clear, focused, and ready to send as an SMS. Feel free to
                add emojis to make it more engaging!
            </Typography>

            <Box sx={styles.textFieldBox}>
                <TextField
                    ref={textFieldRef}
                    counterCurrent={content?.length}
                    counterMax={1600}
                    data-testid="content-field"
                    fullWidth
                    inputProps={{
                        maxLength: 1600,
                        'data-testid': 'content-field',
                    }}
                    label={content ? 'Content' : 'Type your message here'}
                    multiline
                    minRows={10}
                    sx={styles.textFieldInput}
                    value={content}
                    withCounter
                    onChange={(e) => setContent(e.target.value)}
                    onSelect={(e: React.SyntheticEvent) => {
                        const target = e.target as HTMLTextAreaElement;
                        setCursorPosition({
                            start: target.selectionStart || 0,
                            end: target.selectionEnd || 0,
                        });
                    }}
                />

                <Stack direction="row">
                    <Button color="primary" onClick={handleEmojiButtonClick}>
                        <Stack direction="row" alignItems="center" columnGap={1}>
                            <SentimentSatisfiedAlt fontSize="small" />
                            <Typography variant="body2">Emoji</Typography>
                        </Stack>
                    </Button>

                    <Button color="primary" onClick={handleMergeTagButtonClick}>
                        <Stack direction="row" alignItems="center" columnGap={1}>
                            <DataObject fontSize="small" />
                            <Typography variant="body2">Merge tag</Typography>
                        </Stack>
                    </Button>
                </Stack>

                {/* Emoji Picker */}
                <Popover
                    open={Boolean(emojiAnchorEl)}
                    anchorEl={emojiAnchorEl}
                    onClose={handleEmojiPickerClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Picker data={data} onEmojiSelect={handleEmojiSelect} theme="light" previewPosition="none" />
                </Popover>

                {/* Merge Tag Picker */}
                <Menu
                    anchorEl={mergeTagAnchorEl}
                    open={Boolean(mergeTagAnchorEl)}
                    onClose={handleMergeTagPickerClose}
                    PaperProps={{
                        sx: {
                            maxHeight: 300,
                            width: '250px',
                        },
                    }}
                >
                    {isContactColumnsSuccess && orderedMergeTags.length > 0 ? (
                        orderedMergeTags.map((tag: MergeTag) => (
                            <MenuItem key={tag.value} onClick={() => handleMergeTagSelect(tag.value)}>
                                <Typography variant="body2">{getFriendlyName(tag.name)}</Typography>
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem disabled>
                            <Typography variant="body2">Loading merge tags...</Typography>
                        </MenuItem>
                    )}
                </Menu>
            </Box>
        </>
    );
};

export default React.memo(SmsEditor);
